import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SnoozeIcon from '@mui/icons-material/Snooze';
import FlightIcon from '@mui/icons-material/Flight';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import { InboxItem } from '../types';
import { AutocompleteAircraft } from '../../Settings/HangarSettings/AutocompleteAircraft';
import { SnoozePopover } from './SnoozePopover';

export interface BulkActionBarProps {
  selectedItems: InboxItem[];
  hangars: Array<{ id: string; name: string }>;
  ramps: Array<{ id: string; name: string }>;
  onBulkResolve: (aircraftId: string, aircraftName: string) => Promise<void>;
  onBulkAssignHangar: (hangarId: string, hangarName: string) => Promise<void>;
  onBulkAssignRamp: (rampId: string, rampName: string) => Promise<void>;
  onBulkSnooze: (until: Date) => Promise<void>;
  onClearSelection: () => void;
  isProcessing?: boolean;
}

/**
 * Floating action bar for bulk operations on selected items
 */
export function BulkActionBar({
  selectedItems,
  hangars,
  ramps,
  onBulkResolve,
  onBulkAssignHangar,
  onBulkAssignRamp,
  onBulkSnooze,
  onClearSelection,
  isProcessing = false,
}: BulkActionBarProps) {
  const { t } = useTranslation();
  const [snoozeAnchor, setSnoozeAnchor] = useState<HTMLElement | null>(null);
  const [showAircraftPicker, setShowAircraftPicker] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  if (selectedItems.length === 0) return null;

  const quarantineCount = selectedItems.filter(i => i.item_type === 'quarantine').length;
  const tripCount = selectedItems.filter(i => i.item_type === 'unassigned_trip').length;

  const handleAircraftSelect = async (aircraft: any) => {
    if (!aircraft) return;
    setShowAircraftPicker(false);
    await onBulkResolve(aircraft.id, `${aircraft.make} ${aircraft.model}`);
  };

  const handleSnooze = async (until: Date) => {
    setSnoozeAnchor(null);
    await onBulkSnooze(until);
  };

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1300,
        px: 3,
        py: 1.5,
        borderRadius: 3,
        bgcolor: '#1a1a2e',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minWidth: 400,
      }}
    >
      {/* Selection count */}
      <Typography variant="body2" fontWeight={600}>
        {t('screens.operationsInbox.bulkActions.selected', { count: selectedItems.length })}
        {quarantineCount > 0 && tripCount > 0 && (
          <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
            {t('screens.operationsInbox.bulkActions.breakdown', { quarantineCount, tripCount })}
          </Typography>
        )}
      </Typography>

      <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />

      {/* Bulk Aircraft Mapping (only for quarantine items) */}
      {quarantineCount > 0 && (
        <>
          {showAircraftPicker ? (
            <Box sx={{ width: 220, bgcolor: 'white', borderRadius: 1 }}>
              <AutocompleteAircraft
                label={t('screens.operationsInbox.bulkActions.mapAllTo')}
                onSelect={handleAircraftSelect}
              />
            </Box>
          ) : (
            <Button
              size="small"
              startIcon={<FlightIcon />}
              onClick={() => setShowAircraftPicker(true)}
              disabled={isProcessing}
              sx={{ color: 'white', textTransform: 'none' }}
            >
              {t('screens.operationsInbox.bulkActions.buttons.mapToAircraft')}
            </Button>
          )}
        </>
      )}

      {/* Bulk Location Assignment (only for trips) */}
      {tripCount > 0 && (
        <>
          {showLocationPicker ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {hangars.slice(0, 3).map(h => (
                <Button
                  key={h.id}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setShowLocationPicker(false);
                    onBulkAssignHangar(h.id, h.name);
                  }}
                  disabled={isProcessing}
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none', fontSize: 11 }}
                >
                  {h.name}
                </Button>
              ))}
              {ramps.slice(0, 2).map(r => (
                <Button
                  key={r.id}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setShowLocationPicker(false);
                    onBulkAssignRamp(r.id, r.name);
                  }}
                  disabled={isProcessing}
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none', fontSize: 11 }}
                >
                  {r.name}
                </Button>
              ))}
            </Box>
          ) : (
            <Button
              size="small"
              startIcon={<LocationOnIcon />}
              onClick={() => setShowLocationPicker(true)}
              disabled={isProcessing}
              sx={{ color: 'white', textTransform: 'none' }}
            >
              {t('screens.operationsInbox.bulkActions.buttons.assignLocation')}
            </Button>
          )}
        </>
      )}

      {/* Bulk Snooze */}
      <Button
        size="small"
        startIcon={<SnoozeIcon />}
        onClick={(e) => setSnoozeAnchor(e.currentTarget)}
        disabled={isProcessing}
        sx={{ color: 'white', textTransform: 'none' }}
      >
        {t('screens.operationsInbox.bulkActions.snoozeAll')}
      </Button>
      <SnoozePopover
        anchorEl={snoozeAnchor}
        onClose={() => setSnoozeAnchor(null)}
        onSnooze={handleSnooze}
      />

      {/* Processing indicator */}
      {isProcessing && <CircularProgress size={20} sx={{ color: 'white' }} />}

      {/* Clear selection */}
      <Button
        size="small"
        onClick={onClearSelection}
        disabled={isProcessing}
        sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 'auto', p: 0.5 }}
      >
        <CloseIcon fontSize="small" />
      </Button>
    </Paper>
  );
}
